import { GPUInstanceType } from "types/InstanceTypes";
import React, { useEffect, useRef, useState } from "react";
import "../style.scss";
import NvidiaDGXDataCenter from "assets/img/png/dgxDatacenter.png";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import AdvancedSelector, { type SelectedGPUVariant } from "./Advanced/index";
import LegacyAdvancedSelector from "./Advanced/Legacy";
import ComputeLoader from "./ComputeLoader";
import SelectedInstanceDetails from "./SelectedInstanceDetails";
import { isDgxSpark } from "utils/instanceTypes";
import {
  useFreeTier,
  getStoragePricing,
  getRunningTotalDisplayPrice,
  isFreeTierInstance,
  getBasePriceAmount,
  FREE_TIER_COLOR,
} from "utils/freeTierUtils";
import { FreeTierInfoTooltip } from "components/FreeTier";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";

const FEATURE_FLAG_RESOLUTION_TIMEOUT_MS = 3000;

const getComputeFamilyForInstance = (
  instance: GPUInstanceType | undefined,
): string | undefined => {
  if (!instance) return undefined;
  if (getPrimaryInstanceCloudCred(instance)?.id === "dgxc-gcp-1") {
    return "dgx-H100";
  }
  return instance.supported_gpus?.[0]?.name || "CPU";
};

export interface ComputeSelectorProps {
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  selectedInstance: GPUInstanceType | undefined;
  setStorage(size: string): void;
  storage: string;
  containerSelected: ContainerSelected;
  selectedCloudName: string;
  displayGPUInstances: GPUInstanceType[] | undefined;
  isLoadingInstances: boolean;
  allGPUInstances: GPUInstanceType[] | undefined;
  cpuInstances: GPUInstanceType[];
  persistentOnly: boolean;
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  hideDgxc?: boolean;
  locationName?: string | null;
  pickerAllowedLocations?: Set<string> | null;
  animateLoaderCarouselLayout?: boolean;
  syncGpuSelectionToUrl?: boolean;
  initialSelectedChip?: string;
}

const ComputeSelector: React.FC<ComputeSelectorProps> = ({
  selectedInstance,
  setSelectedInstance,
  setStorage,
  storage,
  containerSelected,
  selectedCloudName,
  displayGPUInstances,
  isLoadingInstances,
  allGPUInstances,
  cpuInstances,
  hideDgxc,
  persistentOnly = false,
  flexiblePortControlOnly = false,
  flexibleStorageOnly = false,
  locationName,
  pickerAllowedLocations,
  animateLoaderCarouselLayout,
  syncGpuSelectionToUrl,
  initialSelectedChip,
}) => {
  const redesignedPickerEnabled = useIdentityFeatureFlagEnabled(
    POSTHOG_IDENTITY_FLAGS.GPU_PICKER_REDESIGN,
  );
  const [featureFlagResolutionTimedOut, setFeatureFlagResolutionTimedOut] =
    useState(false);
  const [selectedGPUVariant, setSelectedGPUVariant] = useState<
    SelectedGPUVariant | undefined
  >();
  const [selectedComputeFamily, setSelectedComputeFamily] = useState<
    string | undefined
  >(initialSelectedChip ?? getComputeFamilyForInstance(selectedInstance));
  const focusPickerAfterBackRef = useRef(false);
  const focusDetailsAfterSelectionRef = useRef(false);
  const selectedDetailsHeadingRef = useRef<HTMLHeadingElement>(null);
  const { isEntitled } = useFreeTier();
  const isComputeFree =
    isEntitled &&
    isFreeTierInstance(getBasePriceAmount(selectedInstance, locationName));
  const {
    totalCost: storageCostForTotal,
    withinFreeTier: isStorageWithinFreeTier,
  } = getStoragePricing(selectedInstance, Number(storage));
  const isStorageFree = isEntitled && isStorageWithinFreeTier;
  const runningCost = getRunningTotalDisplayPrice(
    selectedInstance,
    Number(storage),
    locationName,
  );

  useEffect(() => {
    const img = new Image();
    img.src = NvidiaDGXDataCenter;
  }, []);

  useEffect(() => {
    if (redesignedPickerEnabled !== undefined) return;

    const timer = window.setTimeout(
      () => setFeatureFlagResolutionTimedOut(true),
      FEATURE_FLAG_RESOLUTION_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [redesignedPickerEnabled]);

  useEffect(() => {
    if (redesignedPickerEnabled !== true) return;

    const shouldFocusDetails =
      !!selectedInstance && focusDetailsAfterSelectionRef.current;
    const shouldFocusPicker =
      !selectedInstance && focusPickerAfterBackRef.current;
    if (!shouldFocusDetails && !shouldFocusPicker) return;

    focusDetailsAfterSelectionRef.current = false;
    focusPickerAfterBackRef.current = false;
    const animationFrame = window.requestAnimationFrame(() => {
      if (shouldFocusDetails) {
        selectedDetailsHeadingRef.current?.focus();
      } else {
        document.getElementById("compute-family-heading")?.focus();
      }
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [redesignedPickerEnabled, selectedInstance]);

  const handleRedesignedInstanceSelection = (
    instance: GPUInstanceType | undefined,
  ) => {
    focusDetailsAfterSelectionRef.current = !!instance;
    focusPickerAfterBackRef.current = !instance;
    const family = getComputeFamilyForInstance(instance ?? selectedInstance);
    if (family) setSelectedComputeFamily(family);
    setSelectedInstance(instance);
  };

  return (
    <div className="flex flex-col">
      {!selectedInstance && (
        <section>
          <div className="content-placeholder">
            <div className="flex flex-col">
              {redesignedPickerEnabled === undefined &&
              !featureFlagResolutionTimedOut ? (
                <div
                  role="status"
                  aria-live="polite"
                  aria-label="Loading compute picker"
                  className="py-20"
                >
                  <ComputeLoader
                    animateCarouselLayout={animateLoaderCarouselLayout}
                  />
                </div>
              ) : redesignedPickerEnabled ? (
                <AdvancedSelector
                  allGPUInstances={allGPUInstances}
                  displayGPUInstances={displayGPUInstances}
                  cpuInstances={cpuInstances}
                  setSelectedInstance={handleRedesignedInstanceSelection}
                  containerSelected={containerSelected}
                  selectedCloudName={selectedCloudName}
                  isLoadingInstances={isLoadingInstances}
                  hideDgxc={hideDgxc || false}
                  persistentOnly={persistentOnly}
                  flexiblePortControlOnly={flexiblePortControlOnly}
                  flexibleStorageOnly={flexibleStorageOnly}
                  animateLoaderCarouselLayout={animateLoaderCarouselLayout}
                  syncGpuSelectionToUrl={syncGpuSelectionToUrl}
                  pickerAllowedLocations={pickerAllowedLocations}
                  selectedFamily={selectedComputeFamily}
                  setSelectedFamily={setSelectedComputeFamily}
                  selectedGPUVariant={selectedGPUVariant}
                  setSelectedGPUVariant={setSelectedGPUVariant}
                />
              ) : (
                <LegacyAdvancedSelector
                  allGPUInstances={allGPUInstances}
                  displayGPUInstances={displayGPUInstances}
                  cpuInstances={cpuInstances}
                  setSelectedInstance={setSelectedInstance}
                  containerSelected={containerSelected}
                  selectedCloudName={selectedCloudName}
                  isLoadingInstances={isLoadingInstances}
                  hideDgxc={hideDgxc || false}
                  persistentOnly={persistentOnly}
                  flexiblePortControlOnly={flexiblePortControlOnly}
                  flexibleStorageOnly={flexibleStorageOnly}
                  animateLoaderCarouselLayout={animateLoaderCarouselLayout}
                  syncGpuSelectionToUrl={syncGpuSelectionToUrl}
                  initialSelectedChip={initialSelectedChip}
                  pickerAllowedLocations={pickerAllowedLocations}
                />
              )}
            </div>
          </div>
        </section>
      )}
      {!!selectedInstance && (
        <>
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-4">
              {redesignedPickerEnabled === true && (
                <button
                  type="button"
                  onClick={() => handleRedesignedInstanceSelection(undefined)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:border-zinc-500 hover:bg-zinc-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back
                </button>
              )}
              <h2
                ref={selectedDetailsHeadingRef}
                tabIndex={-1}
                className="text-lg font-bold text-gray-900 outline-none dark:text-white"
              >
                {isDgxSpark(selectedInstance.type)
                  ? "Hardware - DGX Spark"
                  : "Hardware Configuration"}
              </h2>
            </div>
            {runningCost > 0 && (
              <span className="flex items-center gap-2">
                <span className="text-lg font-semibold text-primary">
                  {isComputeFree && isStorageFree ? (
                    <span className="flex items-center gap-2">
                      <span className="line-through opacity-50">
                        ${runningCost.toFixed(2)}/hr total
                      </span>
                      <span className={FREE_TIER_COLOR}>Free</span>
                    </span>
                  ) : isComputeFree && !isStorageFree ? (
                    <span className="flex items-center gap-2">
                      <span className="line-through opacity-50">
                        ${runningCost.toFixed(2)}/hr
                      </span>
                      <span>${storageCostForTotal.toFixed(2)}/hr total</span>
                    </span>
                  ) : (
                    `$${runningCost.toFixed(2)}/hr total`
                  )}
                </span>
                {isEntitled && <FreeTierInfoTooltip />}
              </span>
            )}
          </div>
          <SelectedInstanceDetails
            selectedInstance={selectedInstance}
            setSelectedInstance={
              redesignedPickerEnabled === true
                ? handleRedesignedInstanceSelection
                : setSelectedInstance
            }
            storage={storage}
            setStorage={setStorage}
            flexiblePortControlOnly={flexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
            locationName={locationName}
          />
        </>
      )}
    </div>
  );
};

export default ComputeSelector;
